import { Divider, List, Skeleton, Stack } from '@mui/material';
import { Fragment } from 'react';
import { useListContext, Pagination } from 'react-admin';
import { ActivityLog } from '@wwf/sdk';
import { ActivityLogMemberNoteCreated } from './ActivityLogMemberNoteCreated';
import { ActivityLogMissionNoteCreated } from './ActivityLogMissionNoteCreated';
import { ActivityLogContextValue } from './types';

type ActivityLogIteratorProps = {
    context: ActivityLogContextValue;
};

export function ActivityLogIterator({ context }: ActivityLogIteratorProps) {
    const {
        data: activities,
        isLoading,
        error,
    } = useListContext<ActivityLog>();

    if (isLoading) {
        return (
            <Stack mt={0.5}>
                {Array.from({ length: 5 }).map((_, index) => (
                    <Stack spacing={2} sx={{ mt: 1 }} key={index}>
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{ alignItems: 'center' }}
                        >
                            <Skeleton
                                variant="circular"
                                width={20}
                                height={20}
                            />
                            <Skeleton width="100%" />
                        </Stack>
                        <Skeleton variant="rectangular" height={50} />
                        <Divider />
                    </Stack>
                ))}
            </Stack>
        );
    }

    if (error) {
        return <div>Error loading activities</div>;
    }

    if (!activities || activities.length === 0) {
        return <div>No activity found</div>;
    }

    return (
        <>
            <List
                sx={{
                    '& .MuiListItem-root': {
                        marginTop: 0,
                        marginBottom: 1,
                    },
                    '& .MuiListItem-root:not(:first-of-type)': {
                        marginTop: 1,
                    },
                }}
            >
                {activities.map((activity, index) => (
                    <Fragment key={index}>
                        <ActivityItem
                            key={activity.id}
                            activity={activity}
                            context={context}
                        />
                        <Divider />
                    </Fragment>
                ))}
            </List>
            <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
        </>
    );
}

function ActivityItem({
    activity,
    context,
}: {
    activity: ActivityLog;
    context: ActivityLogContextValue;
}) {
    switch (activity.activityType) {
        case 'memberNote.created':
            return (
                <ActivityLogMemberNoteCreated
                    activity={activity}
                    context={context}
                />
            );
        case 'missionNote.created':
            return (
                <ActivityLogMissionNoteCreated
                    activity={activity}
                    context={context}
                />
            );
        default:
            activity.activityType satisfies never;
            return null;
    }
}
